import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Compass, MapPin, RotateCcw, Check } from "lucide-react";
import { TRAVELLERS, DURATIONS, INTERESTS, PARISHES, PARISH_NEARBY, EXPERIENCES, DAY_TEMPLATES } from "@/lib/itinerary";
import { IMAGES } from "@/lib/site";
import { Reveal, MaskedLines } from "@/components/motion-primitives";
import { usePageMeta } from "@/components/shared";
import { track } from "@/lib/transport";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    data-testid={testid}
    className={`border px-5 py-3 text-sm transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F] ${
      active ? "border-navy bg-navy text-ivory" : "border-navy/15 bg-white text-navy/70 hover:border-bermuda"
    }`}
  >
    {children}
  </button>
);

export default function PlanYourStay() {
  usePageMeta("Plan Your Bermuda Stay", "Answer four quick questions and get a personal Bermuda mini-itinerary — built around who you're travelling with, what you love and where you're staying.");
  const [traveller, setTraveller] = useState(null);
  const [duration, setDuration] = useState(null);
  const [interests, setInterests] = useState([]);
  const [parish, setParish] = useState("");
  const [itinerary, setItinerary] = useState(null);

  const toggleInterest = (i) => setInterests((list) => (list.includes(i) ? list.filter((x) => x !== i) : [...list, i]));
  const ready = traveller && duration && interests.length > 0 && parish;

  const generate = () => {
    const days = DAY_TEMPLATES[duration];
    const pool = interests.flatMap((i) => EXPERIENCES[i] || []);
    const plan = days.map((label, di) => {
      if (di === 0) {
        return { label, title: `Settle in — ${parish}`, items: PARISH_NEARBY[parish].map((n) => `Close to you: ${n}`) };
      }
      if (di === days.length - 1 && days.length > 2) {
        return { label, title: "Slow morning & departure", items: ["Coffee on the veranda — no agenda", "One last swim if time allows", "Watch a final Bermuda sunset if you're staying the evening"] };
      }
      const picks = [pool[(di - 1) % pool.length], pool[(di) % pool.length]].filter(Boolean);
      return { label, title: label, items: picks.map(([name, blurb]) => `${name} — ${blurb}`) };
    });
    setItinerary(plan);
    track("itinerary_generated", { traveller, duration, interests: interests.join(","), parish });
    setTimeout(() => document.getElementById("itinerary-result")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const reset = () => { setItinerary(null); setTraveller(null); setDuration(null); setInterests([]); setParish(""); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <main>
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="absolute inset-0">
          <img src={IMAGES.tobaccoBay} alt="Tobacco Bay, Bermuda" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        </div>
        <div className="noise-overlay relative z-10 mx-auto max-w-[900px] px-6 pb-20 pt-40 text-center lg:px-10">
          <Reveal><p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda">Guest Tools</p></Reveal>
          <MaskedLines lines={["Plan Your", "Bermuda Stay"]} className="mx-auto mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight" />
          <Reveal delay={0.4}>
            <p className="mx-auto mt-6 max-w-xl text-base text-ivory/75">
              Four quick answers, one personal mini-itinerary — built around your trip and your corner of the island.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[860px] px-6 py-16 lg:px-10" data-testid="plan-form">
        <Reveal>
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">Who are you travelling with?</h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {TRAVELLERS.map((t) => <Chip key={t} active={traveller === t} onClick={() => setTraveller(t)} testid={`plan-traveller-${t.toLowerCase()}`}>{t}</Chip>)}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-10">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">How long are you staying?</h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {DURATIONS.map((d) => <Chip key={d} active={duration === d} onClick={() => setDuration(d)} testid={`plan-duration-${d.replace(/\W/g, "")}`}>{d}</Chip>)}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">What do you love? <span className="normal-case tracking-normal text-navy/40">(pick a few)</span></h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {INTERESTS.map((i) => <Chip key={i} active={interests.includes(i)} onClick={() => toggleInterest(i)} testid={`plan-interest-${i.toLowerCase()}`}>{i}</Chip>)}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">Where are you staying?</h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {PARISHES.map((p) => <Chip key={p} active={parish === p} onClick={() => setParish(p)} testid={`plan-parish-${p.toLowerCase().replace(/[^a-z]/g, "")}`}>{p}</Chip>)}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <button
            onClick={generate}
            disabled={!ready}
            data-testid="plan-generate-btn"
            className="mt-12 inline-flex items-center gap-2.5 bg-navy px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bermuda"
          >
            <Compass size={14} /> Create My Itinerary
          </button>
        </Reveal>
      </section>

      <AnimatePresence>
        {itinerary && (
          <motion.section
            id="itinerary-result"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-sand-light"
            data-testid="itinerary-result"
          >
            <div className="mx-auto max-w-[860px] px-6 py-16 lg:px-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-bermuda-dark">Your Mini Itinerary</p>
              <h2 className="mt-4 font-serif text-3xl font-light text-navy sm:text-4xl">
                {duration} in Bermuda{traveller === "Solo" ? ", solo" : traveller ? `, as a ${traveller.toLowerCase()}` : ""}.
              </h2>
              <div className="mt-4 flex items-start gap-3 border-l-2 border-bermuda bg-white p-5">
                <MapPin size={15} className="mt-0.5 shrink-0 text-bermuda-dark" />
                <p className="text-sm leading-relaxed text-navy/70">
                  You're staying in <strong className="text-navy">{parish}</strong> — here's what we'd recommend within about 15 minutes of your Bermuda Co-Host property, woven through your days below.
                </p>
              </div>

              <div className="mt-10 space-y-6">
                {itinerary.map((day, i) => (
                  <div key={i} className="border border-navy/10 bg-white p-7" data-testid={`itinerary-day-${i}`}>
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-xs text-bermuda-dark">DAY {i + 1}</span>
                      <h3 className="font-serif text-xl text-navy">{day.title}</h3>
                    </div>
                    <ul className="mt-4 space-y-2.5">
                      {day.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm text-navy/70">
                          <Check size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-ocean" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <button onClick={reset} data-testid="plan-reset-btn" className="inline-flex items-center gap-2 border border-navy/20 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                  <RotateCcw size={12} /> Start Over
                </button>
                <Link to="/guest-support" data-testid="plan-cohost-link" className="inline-flex items-center gap-2 bg-navy px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
                  Ask Your Co-Host for Local Secrets
                </Link>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
